import { useMemo, useState, useEffect } from 'react';
/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';

import './style.scss';
import { Tooltip } from '@mui/material';

const TIME = 60;

const VerificationCodeInput = ({
    toVerify,
    type = 'email',
    executeRecaptcha,
    code,
    setCode,
    verificationError,
    emailError,
    hasEmail,
    onEditProfileForm = false,
    from = '',
    setEmailErrorOnSendCode = (e) => { }
}) => {
    const [error, setError] = useState('');
    const [busy, setBusy] = useState(false);
    const [hasCode, setHasCode] = useState(false);
    const [canResend, setCanResend] = useState(false);
    const [resendTime, setResendTime] = useState(TIME);
    const [resendInterval, setResendInterval] = useState(null);
    const [isHuman, setIsHuman] = useState('');

    const disableVerificationCode = useMemo(() => {
        return emailError || !hasEmail || !toVerify || busy;
    }, [emailError, hasEmail, toVerify, busy]);

    const handleCodeChange = (e) => {
        const value = e.target.value;
        setCode(value);
        setError('');
    };

    const generateRecaptcha = async () => {
        if (!executeRecaptcha) {
            console.log('Execute recaptcha not yet available in signup');
        } else {
            const token = await executeRecaptcha('signup');
            setIsHuman(token);
        }
    };

    useEffect(() => {
        if (isHuman) {
            getCode();
        }
    }, [isHuman]);

    useEffect(() => {
        if (error && hasEmail) {
            setError('');
        }
    }, [hasEmail]);

    const getCode = async () => {
        setBusy(true);
        await axios.post('/api/send-code', {
            is_human: isHuman,
            to_verify: toVerify,
            type,
            from
        })
            .then((response) => {
                setBusy(false);
                setHasCode(true);
                setEmailErrorOnSendCode('');
            })
            .catch((error) => {
                if (error.response.status === 422) {
                    if (error.response?.data.errors.to_verify.length > 0) {
                        setError(error.response.data.errors.to_verify[0]);
                        setEmailErrorOnSendCode(error.response.data.errors.to_verify[0]);
                        // if (error.response.data.errors.to_verify[0] === "The to verify must be a valid email address.") {
                        //     setError('Invalid email');
                        //     setEmailErrorOnSendCode('Invalid email')
                        // } else if (error.response.data.errors.to_verify[0] !== "The email has already been taken") {
                        //     setError('Email is required');
                        //     setEmailErrorOnSendCode('Email is required');
                        // }
                    }
                }
                setBusy(false);
            });
    };

    const runInterval = () => {
        const timeToResend = setInterval(() => {
            setResendTime(resendTime => resendTime - 1);
        }, 1000);
        setResendInterval(timeToResend);

        setTimeout(() => {
            setCanResend(true);
            setHasCode(false);
        }, TIME);
    };

    useEffect(() => {
        let mounted = true;

        if (hasCode) {
            setCanResend(false);
            runInterval();
        }

        return () => {
            mounted -= false;
        };
    }, [hasCode]);

    useEffect(() => {
        if (resendTime <= 0 && resendInterval) {
            clearInterval(resendInterval);
            setResendTime(TIME);
            setCanResend(false);
        }

        return () => {
        };
    }, [resendTime]);

    const [showTooltip, setShowTooltip] = useState(false);
    const handleTooltip = () => {
        setShowTooltip(showTooltip => !showTooltip);
    };

    return <div className='verification-code-input'>
        <div className={`form-group${error ? ' has-error' : ''}`}>
            <input
                name="verificationCodeInput"
                id="verificationCodeInput"
                type="text"
                placeholder={`${onEditProfileForm ? "Enter code" : "Enter email verification code"}`}
                className={`form-control${error ? ' is-invalid' : ''}`}
                onChange={handleCodeChange}
                value={code}
                disabled={disableVerificationCode}
                maxLength={6}
                autoComplete="one-time-code"
            />
            {verificationError ? <div className="invalid-feedback">{verificationError}</div> : null}
        </div>

        <div css={css`
            position: relative;
            margin-top: 12px;
            text-align: left;
        `}>
            <a
                type={`button`}
                className={`get-email-code${disableVerificationCode ? ' disabled' : ''}${canResend ? ' text-grey cursor-default' : ''}`} onClick={!disableVerificationCode && !canResend ? generateRecaptcha : null}
                css={css`
                    width: auto !important;
                    margin-top: 0;
                    padding-top: 0;
                `}
            >
                {
                    !canResend
                        ? 'Send verification code'
                        : <span
                            className={'email-verification-notice'}
                            css={css`
                    display: inline-block;
                `}
                        >
                            <p>{`The verification code was sent to your email.`}</p>
                            <p>{`If you have not received the email, please check your email spam or junk folder.`}</p>
                        </span>
                }
            </a>
            {
                !canResend
                    ? <Tooltip
                        id='code-tooltip'
                        title='Please check your junk and spam folders.'
                        enterTouchDelay={0}
                        disableInteractive
                        arrow
                        open={showTooltip}
                    >
                        <span
                            css={css`
                                padding: 0;
                                color: #0097fe !important;
                                border: 0;
                                outline: 0;
                                background: transparent;
                                margin-left: 6px;
                            `}
                            onClick={handleTooltip}
                        >
                            <i className="fa-regular fa-circle-info"></i>
                        </span>
                    </Tooltip>
                    : null
            }
        </div>
    </div >;
};

export default VerificationCodeInput;
